import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Plus, Trash2, Image, Hash, ToggleLeft, FileText } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useSessionTestPaths } from '@/hooks/useOptiBayTestPaths';
import { useSessionEvidence, useRemoveEvidence } from '@/hooks/useOptiBayEvidence';
import { useOptiBayTestPaths as useTestPaths } from '@/hooks/useOptiBayTaxonomy';
import { OptiBayEvidenceCapture } from './OptiBayEvidenceCapture';
import { OptiBayTrainingHints } from './OptiBayTrainingHints';
import type { OptiBaySession, OptiBaySessionEvidence, OptiBayEvidenceType } from '@/types/optibay';
import { EVIDENCE_TYPE_LABELS } from '@/types/optibay';
import { cn } from '@/lib/utils';

interface OptiBayEvidenceStepProps {
  session: OptiBaySession;
  onNext: () => void;
  onBack: () => void;
}

const EVIDENCE_ICONS: Record<string, typeof Image> = {
  photo: Image,
  waveform: Image,
  scan_data: Image,
  alignment: Image,
  meter_reading: Hash,
  numeric: Hash,
  boolean: ToggleLeft,
  note: FileText,
};

export function OptiBayEvidenceStep({ session, onNext, onBack }: OptiBayEvidenceStepProps) {
  const { data: sessionTestPaths = [] } = useSessionTestPaths(session.id);
  const { data: allEvidence = [] } = useSessionEvidence(session.id);
  const { data: testPathDefs = [] } = useTestPaths(
    session.system_family_id || null,
  );
  const removeEvidence = useRemoveEvidence();

  const [captureForPathId, setCaptureForPathId] = useState<string | null>(null);

  const getTestPathLabel = (testPathId: string) => {
    return testPathDefs.find(tp => tp.id === testPathId)?.label || 'Test';
  };

  const getEvidenceForPath = (sessionTestPathId: string) => {
    return allEvidence.filter(e => e.session_test_path_id === sessionTestPathId);
  };

  const totalEvidence = allEvidence.length;

  const handleRemove = (ev: OptiBaySessionEvidence) => {
    removeEvidence.mutate({
      id: ev.id,
      sessionId: ev.session_id,
      sessionTestPathId: ev.session_test_path_id,
    });
  };

  const renderEvidenceItem = (ev: OptiBaySessionEvidence) => {
    const Icon = EVIDENCE_ICONS[ev.evidence_type] || FileText;
    return (
      <div
        key={ev.id}
        className="flex items-center gap-2 rounded-md border px-3 py-2 text-xs"
      >
        <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-medium">
            {ev.label || EVIDENCE_TYPE_LABELS[ev.evidence_type as OptiBayEvidenceType] || ev.evidence_type}
          </span>
          {ev.value_numeric !== null && (
            <span className="ml-1 text-muted-foreground">
              {ev.value_numeric}{ev.unit ? ` ${ev.unit}` : ''}
            </span>
          )}
          {ev.value_boolean !== null && (
            <Badge variant={ev.value_boolean ? 'default' : 'secondary'} className="ml-1.5 text-[10px]">
              {ev.value_boolean ? 'Yes' : 'No'}
            </Badge>
          )}
          {ev.value_text && (
            <p className="text-muted-foreground mt-0.5 truncate">{ev.value_text}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 shrink-0 text-destructive/70 hover:text-destructive"
          onClick={() => handleRemove(ev)}
          disabled={removeEvidence.isPending}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Capture Evidence</h4>
        <Badge variant="secondary" className="text-xs">
          {totalEvidence} item{totalEvidence !== 1 ? 's' : ''}
        </Badge>
      </div>

      {sessionTestPaths.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">
          No test paths selected. Go back to select tests first.
        </p>
      ) : (
        <Accordion type="multiple" className="space-y-1" defaultValue={sessionTestPaths.map(p => p.id)}>
          {sessionTestPaths.map(stp => {
            const evidence = getEvidenceForPath(stp.id);
            return (
              <AccordionItem key={stp.id} value={stp.id} className="border rounded-lg px-1">
                <AccordionTrigger className="py-2.5 px-2 text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{getTestPathLabel(stp.test_path_id)}</span>
                    {evidence.length > 0 && (
                      <Badge variant="outline" className="text-[10px]">
                        {evidence.length}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2 pb-3">
                  <div className="space-y-2">
                    {evidence.map(renderEvidenceItem)}

                    {captureForPathId === stp.id ? (
                      <OptiBayEvidenceCapture
                        sessionId={session.id}
                        sessionTestPathId={stp.id}
                        shopId={session.shop_id}
                        onClose={() => setCaptureForPathId(null)}
                        onSuccess={() => setCaptureForPathId(null)}
                      />
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full text-xs"
                        onClick={() => setCaptureForPathId(stp.id)}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add Evidence
                      </Button>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}

      <OptiBayTrainingHints session={session} className="mt-2" />

      <div className="flex justify-between pt-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button
          size="sm"
          onClick={onNext}
          disabled={totalEvidence === 0}
        >
          Continue to Guidance
          <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
